import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, DatePicker, InputNumber, Card, Space, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../../services/api';
import localforage from 'localforage';
import { Store } from '../../types';

const { Option } = Select;

const StoreForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [storeType, setStoreType] = useState<string>('自用');
  const isEditing = !!id;

  // 自動保存的鍵
  const getAutoSaveKey = () => `store_form_${id || 'new'}`;

  // 加載店鋪數據
  useEffect(() => {
    const fetchStore = async () => {
      if (isEditing) {
        setLoading(true);
        try {
          const data = await api.stores.getById(id as string);

          // 處理日期字段
          if (data.rental_start_date) {
            (data as any).rental_start_date = moment(data.rental_start_date);
          }
          if (data.rental_end_date) {
            (data as any).rental_end_date = moment(data.rental_end_date);
          }
          if (data.rental_payment_date) {
            (data as any).rental_payment_date = moment(data.rental_payment_date);
          }

          form.setFieldsValue(data);
          setStoreType(data.type);
        } catch (error) {
          console.error('Error fetching store:', error);
          message.error(t('common.error'));
        } finally {
          setLoading(false);
        }
      } else {
        // 嘗試從本地存儲加載草稿
        const savedData = await localforage.getItem(getAutoSaveKey()) as Partial<Store> | null;
        if (savedData) {
          form.setFieldsValue(savedData);
          if (savedData.type) {
            setStoreType(savedData.type);
          }
        }
      }
    };

    fetchStore();
  }, [id, form, isEditing, t]);

  // 自動保存表單數據
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      const formValues = form.getFieldsValue();
      if (Object.keys(formValues).length > 0) {
        await localforage.setItem(getAutoSaveKey(), formValues);
      }
    }, 30000); // 每 30 秒保存一次

    return () => clearInterval(autoSaveInterval);
  }, [form]);

  // 提交表單
  const handleSubmit = async (values: Partial<Store>) => {
    setSubmitting(true);
    try {
      // 處理日期字段
      if (values.rental_start_date) {
        values.rental_start_date = (values.rental_start_date as any).format('YYYY-MM-DD');
      }
      if (values.rental_end_date) {
        values.rental_end_date = (values.rental_end_date as any).format('YYYY-MM-DD');
      }
      if (values.rental_payment_date) {
        values.rental_payment_date = (values.rental_payment_date as any).format('YYYY-MM-DD');
      }

      if (isEditing) {
        await api.stores.update(id as string, values);
      } else {
        await api.stores.create(values);
        // 清除本地存儲的草稿
        await localforage.removeItem(getAutoSaveKey());
      }

      message.success(t('common.success'));
      navigate('/stores');
    } catch (error) {
      console.error('Error saving store:', error);
      message.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // 處理類型變更
  const handleTypeChange = (value: string) => {
    setStoreType(value);
  };

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(isEditing ? `/stores/${id}` : '/stores')}
        >
          {t('common.back')}
        </Button>
      </Space>

      <Card title={isEditing ? t('stores.editStore') : t('stores.addStore')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: '自用',
            status: '營業中',
          }}
        >
          <Form.Item
            name="store_id"
            label={t('stores.storeId')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="platform"
            label={t('stores.platform')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('stores.name')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="short_name"
            label={t('stores.shortName')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="type"
            label={t('stores.type')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select onChange={handleTypeChange}>
              <Option value="自用">{t('stores.types.self')}</Option>
              <Option value="月租">{t('stores.types.monthly')}</Option>
              <Option value="季度">{t('stores.types.quarterly')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label={t('stores.status')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select>
              <Option value="營業中">{t('stores.statuses.operating')}</Option>
              <Option value="停業">{t('stores.statuses.suspended')}</Option>
              <Option value="失效">{t('stores.statuses.expired')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="owner"
            label={t('stores.owner')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          {(storeType === '月租' || storeType === '季度') && (
            <>
              <Form.Item
                name="rental_amount"
                label={t('stores.rentalAmount')}
                rules={[{ required: true, message: t('common.error') }]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="rental_start_date"
                label={t('stores.rentalStartDate')}
                rules={[{ required: true, message: t('common.error') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="rental_end_date"
                label={t('stores.rentalEndDate')}
                rules={[{ required: true, message: t('common.error') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="rental_payment_status"
                label={t('stores.rentalPaymentStatus')}
                rules={[{ required: true, message: t('common.error') }]}
              >
                <Select>
                  <Option value="已支付">{t('stores.paymentStatuses.paid')}</Option>
                  <Option value="未支付">{t('stores.paymentStatuses.unpaid')}</Option>
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.rental_payment_status !== currentValues.rental_payment_status
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('rental_payment_status') === '已支付' && (
                    <Form.Item
                      name="rental_payment_date"
                      label={t('stores.rentalPaymentDate')}
                      rules={[{ required: true, message: t('common.error') }]}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SaveOutlined />}
            >
              {t('common.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default StoreForm;
